import { computeDesignSchemeSetSurfaceMetrics } from '@data/buildingProjects/DesignSchemes/SurfaceComputationHelper';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { useDesignSchemesBucket } from '@gui/store/persistent/persistentStore';
import { CircularProgress } from '@mui/material';
import { formatMetricNumber } from 'Utils/MetricFormatting';
import { useEffect, useMemo, useState } from 'react';
import { MetricRow } from './MetricTable';

export function useDesignSchemesMetricRows(): MetricRow[] {
    const selectedDesignSchemeSetId = useDesignSchemesBucket((s) => s.selectedSetId);
    const selectedDesignSchemeSetState = useDesignSchemesBucket((s) => (s.selectedSetId ? s.sets[s.selectedSetId] : undefined));
    const [revision, setRevision] = useState(0);

    // =====================================
    // UPDATE LOGIC
    // =====================================

    // Handle async DesignScheme rendering and metric updates:
    useEffect(() => {
        if (!selectedDesignSchemeSetId || !selectedDesignSchemeSetState) return;

        let cancelled = false;
        let wasGenerating = false;

        const tick = () => {
            if (cancelled) return;

            const designSchemeSet = ControllerManager.getInstance()
                .getDesignSchemeController()
                .getDesignSchemeSet(selectedDesignSchemeSetId);
            const schemes = designSchemeSet?.getDesignSchemes() ?? [];
            const isGenerating = schemes.some((scheme) => scheme.getRenderer().isGenerating());

            if (isGenerating || wasGenerating) {
                setRevision((current) => current + 1);
            }

            wasGenerating = isGenerating;
        };

        tick();
        const intervalId = window.setInterval(tick, 150);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [selectedDesignSchemeSetId, selectedDesignSchemeSetState]);

    // =====================================
    // USER INTERFACE
    // =====================================

    return useMemo(() => {
        if (!selectedDesignSchemeSetId || !selectedDesignSchemeSetState) return [];

        const designSchemeSet = ControllerManager.getInstance().getDesignSchemeController().getDesignSchemeSet(selectedDesignSchemeSetId);
        if (!designSchemeSet) return [];

        const isAnyRendererGenerating = designSchemeSet.getDesignSchemes().some((scheme) => scheme.getRenderer().isGenerating());
        const loadingValue = <CircularProgress size={14} thickness={5} sx={{ verticalAlign: 'middle' }} />;
        const metrics = isAnyRendererGenerating ? null : computeDesignSchemeSetSurfaceMetrics(designSchemeSet);
        if (!isAnyRendererGenerating && metrics != null && metrics.totalSurfaceM2 < 0) return [];

        const rows: MetricRow[] = [
            {
                label: 'Design Schemes',
                value: designSchemeSet.getName(),
                hideUnit: true,
            },
            {
                label: 'Total Design Surface',
                value: isAnyRendererGenerating ? loadingValue : formatMetricNumber(metrics!.totalSurfaceM2),
                unit: 'm²',
                tooltip: 'Sum of all visible exterior design-scheme mesh surfaces in the selected set.',
                dividerAfter: true,
            },
            {
                label: 'Opaque Surface',
                value: isAnyRendererGenerating ? loadingValue : formatMetricNumber(metrics!.opaqueSurfaceM2),
                unit: 'm²',
            },
            {
                label: 'Glazed Surface',
                value: isAnyRendererGenerating ? loadingValue : formatMetricNumber(metrics!.glazedSurfaceM2),
                unit: 'm²',
            },
            {
                label: 'PV Surface',
                value: isAnyRendererGenerating ? loadingValue : formatMetricNumber(metrics!.pvSurfaceM2),
                unit: 'm²',
            },
        ];

        if (isAnyRendererGenerating || (metrics != null && metrics.otherSurfaceM2 > 0)) {
            rows.push({
                label: 'Other Surface',
                value: isAnyRendererGenerating ? loadingValue : formatMetricNumber(metrics!.otherSurfaceM2),
                unit: 'm²',
            });
        }

        return rows;
    }, [selectedDesignSchemeSetId, selectedDesignSchemeSetState, revision]);
}
